// Stale-chunk auto-recovery — PURE decision logic (no window/DOM/sessionStorage
// side effects here, so it is fully unit-testable). The impure wrapper that
// reads sessionStorage, records telemetry, and calls location.reload() lives in
// src/utils/staleChunk.ts and delegates the "reload or surface?" call here.
//
// Background (Error Center ticket, Jul 4): a user had the SPA open across a
// deploy. Hashed lazy chunks rotate per deploy, so a tab still running the OLD
// index.js requests an OLD chunk hash (e.g. Duplicates-DTe9sNgQ.js) when the
// user navigates to a not-yet-loaded route. The functions/ asset-miss guard
// correctly 404s the missing asset (X-Asset-Miss + no-store), so the dynamic
// import() rejects with "Failed to fetch dynamically imported module" and the
// crash surfaces to the user. The fix: on the FIRST such failure, perform ONE
// guarded reload (fresh no-store index.html pulls the new chunk graph). The
// guard is keyed by (bundleVersion, href) so:
//   * first failure at a version+URL  -> reload (recover the stale tab)
//   * second failure at the SAME version+URL -> SURFACE (a genuinely-broken
//     deploy can never spin an infinite reload loop)
//   * a new deploy (new bundleVersion) -> different key -> fresh reload budget

// Minification-proof marker string literal for live-bundle verification.
export const STALE_CHUNK_RELOAD_MARKER = 'stale-chunk-reload-guard-v2'

// Messages emitted by browsers / Vite when a dynamic import or module preload
// fails because its hashed chunk no longer exists (post-deploy) or was served
// the SPA fallback HTML instead of JS.
const STALE_CHUNK_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Failed to load module script|dynamically imported module|ChunkLoadError|Loading chunk \d+ failed/i

export function isStaleChunkError(msg: unknown): boolean {
  return typeof msg === 'string' && STALE_CHUNK_RE.test(msg)
}

// One guard entry per (bundleVersion, href). A new deploy rotates the entry
// bundle hash => new bundleVersion => new key => a fresh single-reload budget,
// so distinct deploys never share a guard. Keeping the full href (route hash
// included) scopes the budget to the specific route whose chunk failed.
export function staleChunkReloadKey(bundleVersion: string, href: string): string {
  return `har_stale_chunk_reload:${bundleVersion || 'dev'}:${href || ''}`
}

export type StaleChunkDecision = {
  // Whether the error looks like a stale/failed chunk load at all.
  isStaleChunk: boolean
  // Whether the caller should perform the one-time recovery reload. False when
  // it is not a stale-chunk error, OR when we already reloaded once for this
  // exact bundleVersion+href (surface the error instead of looping).
  shouldReload: boolean
  // sessionStorage key the caller should set (before reload) / inspect.
  key: string
}

// Pure: given the error, the current bundle version + URL, and whether the
// one-shot guard flag is already set for that key, decide reload-vs-surface.
// `assumeStale` short-circuits detection for callers that already KNOW the
// failure is a chunk load failure (e.g. Vite's `vite:preloadError` event).
export function decideStaleChunkReload(params: {
  message?: unknown
  assumeStale?: boolean
  bundleVersion: string
  href: string
  hasReloadedFlag: boolean
}): StaleChunkDecision {
  const key = staleChunkReloadKey(params.bundleVersion, params.href)
  const isStaleChunk = params.assumeStale === true || isStaleChunkError(params.message)
  if (!isStaleChunk) return { isStaleChunk: false, shouldReload: false, key }
  // Stale chunk. Reload once; if the flag is already set for this exact
  // version+URL we've reloaded before and it STILL failed -> broken deploy,
  // surface the error rather than loop.
  return { isStaleChunk: true, shouldReload: !params.hasReloadedFlag, key }
}
